/**
 * A search box over the Deal Room's workbooks with a result list under it
 * (DOP-627). Arrow keys move, Enter picks, Escape closes.
 */

import { Loader2, Search } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import type { CatalogDocument } from "../../hooks/useDocumentMention";
import { useWorkbookSearch } from "../../hooks/useWorkbookSearch";

export interface WorkbookSearchProps {
	/** Names the input for screen readers: "Find the raw file". */
	label: string;
	onPick: (doc: CatalogDocument) => void;
	onCancel?: () => void;
	/** Limit the search to one deal. */
	projectId?: string | null;
	/** Doc ids not to offer: files already chosen for another step. */
	exclude?: readonly string[];
	autoFocus?: boolean;
}

const WorkbookSearch: React.FC<WorkbookSearchProps> = ({
	label,
	onPick,
	onCancel,
	projectId,
	exclude = [],
	autoFocus = false,
}) => {
	const [query, setQuery] = useState("");
	const [highlighted, setHighlighted] = useState(0);
	const { results, loading } = useWorkbookSearch(query, true, projectId);
	const listRef = useRef<HTMLUListElement>(null);
	const shown = results.filter((doc) => !exclude.includes(doc.doc_id));

	useEffect(() => setHighlighted(0), [results]);
	useEffect(() => {
		listRef.current
			?.querySelectorAll<HTMLElement>('[role="option"]')
			[highlighted]?.scrollIntoView({ block: "nearest" });
	}, [highlighted]);

	const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Escape") {
			event.preventDefault();
			onCancel?.();
			return;
		}
		if (shown.length === 0) return;
		if (event.key === "ArrowDown") {
			event.preventDefault();
			setHighlighted((index) => (index + 1) % shown.length);
		} else if (event.key === "ArrowUp") {
			event.preventDefault();
			setHighlighted((index) => (index - 1 + shown.length) % shown.length);
		} else if (event.key === "Enter") {
			event.preventDefault();
			onPick(shown[highlighted]);
		}
	};

	return (
		<div className="rounded-xl border border-gray-200 bg-white">
			<label className="flex h-10 items-center gap-2 border-b border-[#f1f2f4] px-3 text-[13px] text-[#6B7280]">
				<Search className="h-[15px] w-[15px] flex-shrink-0" aria-hidden />
				<input
					value={query}
					onChange={(event) => setQuery(event.target.value)}
					onKeyDown={onKeyDown}
					placeholder="Search Excel files in the Deal Room"
					aria-label={label}
					// eslint-disable-next-line jsx-a11y/no-autofocus
					autoFocus={autoFocus}
					className="min-w-0 flex-1 border-0 p-0 text-sm text-[#111111] outline-none focus:ring-0"
				/>
				{onCancel && (
					<button
						type="button"
						onClick={onCancel}
						className="text-[12.5px] font-medium text-[#5B6472] hover:text-[#111111]"
					>
						Cancel
					</button>
				)}
			</label>
			<ul
				ref={listRef}
				role="listbox"
				aria-label={label}
				className="max-h-56 overflow-y-auto py-1"
			>
				{loading && shown.length === 0 && (
					<li className="flex items-center gap-2 px-3 py-2 text-xs text-[#6B7280]">
						<Loader2 className="h-3 w-3 animate-spin" aria-hidden />
						Searching…
					</li>
				)}
				{!loading && shown.length === 0 && (
					<li className="px-3 py-2 text-xs text-[#6B7280]">
						No Excel files found.
					</li>
				)}
				{shown.map((doc, index) => (
					<li
						key={doc.doc_id}
						role="option"
						aria-selected={index === highlighted}
						onMouseEnter={() => setHighlighted(index)}
						onMouseDown={(event) => {
							// Before the input's blur, so the pick lands.
							event.preventDefault();
							onPick(doc);
						}}
						className={`cursor-pointer px-3 py-2 ${
							index === highlighted ? "bg-[#E6F3F3]" : "hover:bg-gray-50"
						}`}
					>
						<span className="block truncate text-sm font-medium text-[#111111]">
							{doc.filename}
						</span>
						<span className="block truncate text-xs text-[#6B7280]">
							{doc.deal_name}
						</span>
					</li>
				))}
			</ul>
		</div>
	);
};

export default WorkbookSearch;
